import { Button, Container } from "react-bootstrap";
import "./profile_analysis_premium.scss";
import React, { useEffect, useState } from "react";
import WebService from "../../Services/WebService";
import strong from "../../assets/images/icons/strong.svg";
import basic from "../../assets/images/icons/incomplete.svg";
import weak from "../../assets/images/icons/weak.svg";
import moderate from "../../assets/images/icons/moderate.svg";
import expert from "../../assets/images/icons/expert.svg";
import critical from "../../assets/images/icons/critical.svg";
import warning from "../../assets/images/icons/warning.svg";
import info from "../../assets/images/icons/info.svg";
import ok from "../../assets/images/icons/ok.svg";
import high from "../../assets/images/icons/high.svg";
import medium from "../../assets/images/icons/medium.svg";
import low from "../../assets/images/icons/low.svg";
import { FiAlertTriangle, FiArrowRight, FiDownload, FiRefreshCw, FiEdit3 } from "react-icons/fi";
import { BiPencil } from "react-icons/bi";
import {
  AnalysisInfo,
  Improvement,
  KeywordInsight,
  ProfileAnalysisResponse,
  Recommendation,
} from "./profile_analysis_interfaces";
import HelperService from "../../Services/HelperService";
import AILoader from "./ai_loader";
import { useSelector } from "react-redux";
import { LOGIN_TYPE } from "../../assets/constants/constants";
import { useNavigate, useParams } from "react-router-dom";
import UpdateProfileInfoModal from "./UpdateProfileInfoModal";
import UserInputModal from "./UserInputModal";
import { toast } from "react-toastify";

/* severity => { text, bg, c(ring/accent) } — mirrors the premium blade template. */
const SEV: Record<string, { text: string; bg: string; c: string }> = {
  expert: { text: "#15803d", bg: "#dcfce7", c: "#16a34a" },
  strong: { text: "#166534", bg: "#f0fdf4", c: "#22c55e" },
  moderate: { text: "#c2410c", bg: "#fff7ed", c: "#f59e0b" },
  basic: { text: "#b91c1c", bg: "#fef2f2", c: "#ef4444" },
  incomplete: { text: "#b91c1c", bg: "#fef2f2", c: "#ef4444" },
  weak: { text: "#b91c1c", bg: "#fef2f2", c: "#ef4444" },
  critical: { text: "#991b1b", bg: "#fee2e2", c: "#dc2626" },
  warning: { text: "#c2410c", bg: "#fff7ed", c: "#f59e0b" },
  high: { text: "#b91c1c", bg: "#fef2f2", c: "#ef4444" },
  medium: { text: "#c2410c", bg: "#fff7ed", c: "#f59e0b" },
  low: { text: "#166534", bg: "#f0fdf4", c: "#22c55e" },
  info: { text: "#0369a1", bg: "#e0f2fe", c: "#0284c7" },
  ok: { text: "#166534", bg: "#f0fdf4", c: "#22c55e" },
};

const sevMeta = (s?: string) => {
  const k = (s || "").toLowerCase();
  return SEV[k] || { text: "#166534", bg: "#f0fdf4", c: "#22c55e" };
};

const severityIcons: any = {
  strong,
  basic,
  incomplete: basic,
  weak,
  moderate,
  expert,
  critical,
  warning,
  info,
  ok,
  high,
  medium,
  low,
};

/* SVG Icons for category labels */
const IconAreas = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="8" stroke="#dc2626" strokeWidth="2" />
    <path d="M10 6v5" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
    <circle cx="10" cy="14" r="1" fill="#dc2626" />
  </svg>
);

const IconReco = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="8" stroke="#16a34a" strokeWidth="2" />
    <path
      d="M7 10l2 2 4-4"
      stroke="#16a34a"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconInsights = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="8" stroke="#0ea5a0" strokeWidth="2" />
    <path
      d="M10 9v5M10 6h.01"
      stroke="#0ea5a0"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/* Donut Gauge component */
const Gauge: React.FC<{
  pct: number;
  size?: number;
  tw?: number;
  arc?: string;
  track?: string;
  textColor?: string;
  sub?: string;
}> = ({
  pct,
  size = 100,
  tw = 10,
  arc = "#22c55e",
  track = "rgba(255,255,255,0.25)",
  textColor = "#ffffff",
  sub = "/100",
}) => {
    const r = (size - tw) / 2;
    const c = 2 * Math.PI * r;
    const off = c - (Math.min(100, Math.max(0, pct)) / 100) * c;
    return (
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          display: "inline-block",
        }}
      >
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={track}
            strokeWidth={tw}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={arc}
            strokeWidth={tw}
            strokeDasharray={c}
            strokeDashoffset={off}
            strokeLinecap="round"
            fill="none"
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: textColor,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          <span style={{ fontSize: Math.round(size * 0.3) }}>{pct}</span>
          {sub ? (
            <span
              style={{
                fontSize: Math.round(size * 0.13),
                opacity: 0.7,
                marginTop: 2,
              }}
            >
              {sub}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

const AnalysisPremium = () => {
  const [analysisData, setAnalysisData] =
    useState<ProfileAnalysisResponse | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState<any>(null);
  const [fieldsToImprove, setFieldsToImprove] = useState<any>(null);
  const [showUserInputModal, setShowUserInputModal] = useState(false);
  const [selectedImprovementForInput, setSelectedImprovementForInput] =
    useState<Improvement | null>(null);
  const [userInputSectionPage, setUserInputSectionPage] = useState<string>("");
  const [responseBusinessInfoId, setResponseBusinessInfoId] = useState<
    string | number | null
  >(null);
  const [selectedImprovementBizInfoId, setSelectedImprovementBizInfoId] =
    useState<string | number | null>(null);

  const profile_journey_info = useSelector(
    (state: any) => state?.items?.profile_journey_info,
  );
  const registrationIds = useSelector(
    (state: any) => state?.items?.CurrentRegistrationIds,
  );
  const profileDetail = useSelector((state: any) => state?.items?.profileDetail);

  const businessId =
    responseBusinessInfoId ||
    registrationIds?.business_info_id ||
    registrationIds?.business_id ||
    profileDetail?.business_info_id ||
    profileDetail?.basic_info?.entrepreneur_business_details?.business_info_id ||
    profileDetail?.basic_info?.entrepreneur_business_details?.id ||
    profileDetail?.business_id ||
    localStorage.getItem("business_info_id") ||
    localStorage.getItem("business_id");

  const businessUuid =
    registrationIds?.business_uuid ||
    profileDetail?.basic_info?.entrepreneur_business_details?.uuid ||
    profileDetail?.business_uuid ||
    localStorage.getItem("business_uuid");

  const handleOpenUserInputModal = (
    item: Improvement,
    page?: string,
    sectionBizInfoId?: string | number | null,
  ) => {
    setSelectedImprovementForInput(item);
    setUserInputSectionPage(page || "");
    const targetBizInfoId =
      (item as any)?.business_info_id ??
      sectionBizInfoId ??
      responseBusinessInfoId ??
      (analysisData as any)?.business_info_id ??
      (analysisData as any)?.data?.business_info_id ??
      (analysisData as any)?.profile_summary?.business_info_id ??
      profile_journey_info?.business_info_id ??
      profile_journey_info?.data?.business_info_id ??
      profileDetail?.business_info_id ??
      profileDetail?.basic_info?.entrepreneur_business_details?.business_info_id ??
      registrationIds?.business_info_id ??
      null;
    setSelectedImprovementBizInfoId(targetBizInfoId);
    setShowUserInputModal(true);
  };

  const navigate = useNavigate();
  const params = useParams();
  const activeLoginTypes = useSelector(
    (state: any) => state?.items?.storeActiveLoginType,
  );
  const login_type = (
    params?.login_type ||
    activeLoginTypes?.loginType ||
    ""
  ).toLowerCase();
  const account_type = (
    params?.account_type ||
    activeLoginTypes?.account_type ||
    ""
  ).toLowerCase();

  // The {type} slug used by the cached profile-analysis endpoints.
  const getAnalysisTypeSlug = () => {
    if (login_type?.toUpperCase() === LOGIN_TYPE.INVESTOR) return "investor";
    if (account_type === "investment") return "capital-bridge";
    if (account_type === "want-to-grow") return "global-connect";
    if (account_type === "want-to-sell") return "business-exchange";
    return "";
  };

  const getAnalysisResponse = (showLoader: boolean = true) => {
    let action = "";
    if (login_type?.toUpperCase() === LOGIN_TYPE.INVESTOR) {
      action = `investor/profile-analysis`;
    } else {
      if (account_type === "investment") {
        action = `capital-bridge/profile-analysis`;
      } else if (account_type === "want-to-grow") {
        action = `global-connect/profile-analysis`;
      } else if (account_type === "want-to-sell") {
        action = `business-exchange/profile-analysis`;
      }
    }
    if (!action) {
      if (showLoader) setLoadingData(false);
      return;
    }
    if (showLoader) setLoadingData(true);
    WebService.getAPI({
      action: action,
      body: null,
      isShowError: true,
    })
      .then((res: any) => {
        const extractedBizInfoId =
          res?.business_info_id ??
          res?.data?.business_info_id ??
          res?.profile_summary?.business_info_id ??
          res?.data?.profile_summary?.business_info_id ??
          res?.data?.business_id ??
          res?.business_id ??
          null;
        if (extractedBizInfoId) {
          setResponseBusinessInfoId(extractedBizInfoId);
        }
        setAnalysisData(res?.data?.profile_summary ? res.data : res);
      })
      .catch((error: any) => {
        return false;
      })
      .finally(() => {
        if (showLoader) setLoadingData(false);
      });
  };

  useEffect(() => {
    getAnalysisResponse();
  }, [login_type, account_type]);

  const handleDownloadPdf = () => {
    const slug = getAnalysisTypeSlug();
    if (!slug || downloadingPdf) return;
    setDownloadingPdf(true);

    WebService.getAPI({
      action: `profile-analysis/${slug}/pdf`,
      isShowError: true,
    })
      .then((res: any) => {
        const pdfUrl = res?.url ?? res?.data?.url;
        if (!res?.status) {
          toast.error(res?.message);
        }
        if (!pdfUrl) return;

        const downloadName =
          res?.filename ?? res?.data?.filename ?? "analysis-report.pdf";

        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = downloadName;
        a.click();
      })
      .catch(() => { })
      .finally(() => setDownloadingPdf(false));
  };

  const getProfileName = () => {
    const acc = account_type?.toUpperCase();
    if (acc === "INVESTMENT") return "Capital Bridge";
    if (login_type === "INVESTOR" || login_type === "investor") return "Investor";
    if (acc === "WANT-TO-GROW") return "Global Connect";
    if (acc === "WANT-TO-SELL") return "Business Exchange";
    return "";
  };

  const handleContinue = () => {
    const slug = getAnalysisTypeSlug();
    if (slug) {
      WebService.postAPI({
        action: `profile-analysis/${slug}/continue`,
        body: {},
        isShowError: false,
      }).catch(() => false);
    }

    localStorage.setItem("Tab_Selected", "home");
    const loginType = (login_type || "entrepreneur").toLowerCase();
    const accountType = (account_type || "investment").toLowerCase();

    if (loginType === "entrepreneur") {
      navigate(`/${loginType}/${accountType}/home`, { replace: true });
    } else {
      navigate(`/${loginType}/home`, { replace: true });
    }
  };

  const summary = analysisData?.profile_summary;
  const strength = summary?.profile_strength;
  const sMeta = sevMeta(strength?.severity);

  return (
    <section className="analysis-premium pb-4 pt-0">
      {loadingData ? (
        <AILoader />
      ) : (
        <Container fluid>
          {/* ===== Sticky Header Section (Hero + Action Toolbar) ===== */}
          <div className="pa-sticky-header">
            {/* ===== Hero ===== */}
            <div className="pa-hero">
              <div className="d-flex justify-content-between align-items-center flex-column flex-md-row gap-3 w-100">
                <div>
                  <div className="pa-eyebrow">
                    {getProfileName() ? `${getProfileName()} — ` : ""}AI Shark Intelligence Report
                  </div>
                  <h1 className="pa-hero-title">
                    {summary?.name || summary?.company_name
                      ? `${summary?.name || summary?.company_name} — `
                      : ""}
                    Profile Analysis Report
                  </h1>
                  <p className="pa-hero-sub">
                    AI-Powered Analysis &nbsp;•&nbsp; {getProfileName()}
                  </p>
                  {summary?.screening_status && (
                    <span className="pa-screening">
                      <span className="dot" />
                      {summary?.screening_status}
                    </span>
                  )}
                </div>

                <div className="pa-gauge-chip">
                  <Gauge
                    pct={strength?.score || 0}
                    size={78}
                    tw={8}
                    arc="#7ee08a"
                    track="rgba(255,255,255,0.22)"
                    textColor="#ffffff"
                  />
                  <div className="pa-gauge-label">
                    {HelperService.ProperCase(strength?.label || "")}
                  </div>
                  <div className="pa-gauge-exp">
                    {strength?.percentage_to_expert}
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Action toolbar ===== */}
            <div className="pa-toolbar mt-2">
              <div className="d-lg-flex justify-content-between align-items-center">
                <div className="mb-lg-0 mb-2">
                  <h3 className="pa-toolbar-title">
                    {getProfileName() ? `${getProfileName()} ` : ""}AI Shark Intelligence Report
                  </h3>
                  <p className="pa-toolbar-sub">
                    Deep analysis with structured insights and targeted
                    recommendations.
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-1.5 px-3 py-1.5 font-13 text-nowrap"
                    onClick={() => handleDownloadPdf()}
                    disabled={downloadingPdf}
                  >
                    <FiDownload size={15} className="me-2" />
                    <span>{downloadingPdf ? "Preparing..." : "Download PDF"}</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-1.5 px-3 py-1.5 font-13 text-nowrap"
                    onClick={() => getAnalysisResponse()}
                  >
                    <FiRefreshCw size={15} className="me-2" />
                    <span>Re-Analyze</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-1.5 px-3 py-1.5 font-13 text-nowrap"
                    onClick={() => handleContinue()}
                  >
                    <span>Continue</span>
                    <FiArrowRight size={15} className="ms-2" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Scorecard strip ===== */}
          <div className="pa-band mt-3">
            <div className="pa-band-title">Profile Strength Scorecard</div>
            <div className="row g-3 mt-1">
              {analysisData?.analysis_info?.map(
                (sec: AnalysisInfo, index: number) => {
                  const ps = sec?.data?.page_score;
                  const m = sevMeta(ps?.severity);
                  return (
                    <div
                      className="col-6 col-md-3"
                      key={`ring_${index}`}
                    >
                      <div className="pa-ring-card">
                        <Gauge
                          pct={ps?.total_score || 0}
                          size={64}
                          tw={8}
                          arc={m.c}
                          track="#eceeec"
                          textColor={m.text}
                          sub=""
                        />
                        <div className="pa-ring-name">
                          {HelperService.ProperCase(
                            sec?.data?.page || sec?.page || "",
                          )}
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </div>

            {summary?.profile_completeness && (
              <div className="d-flex justify-content-center align-items-center flex-wrap gap-2 mt-3">
                <span className="pa-legend">STANDARD PARAMETERS</span>
                {Object.entries(summary?.profile_completeness).map(
                  ([key, value]) => {
                    const m = sevMeta(key);
                    return (
                      <span
                        key={key}
                        className="pa-legend-pill"
                        style={{ background: m.bg, color: m.text }}
                      >
                        {HelperService.ProperCase(key)}{" "}
                        {String(value).replace("score", "").trim()}
                      </span>
                    );
                  },
                )}
              </div>
            )}
          </div>

          {/* ===== Sections ===== */}
          {analysisData?.analysis_info?.map(
            (sec: AnalysisInfo, index: number) => {
              if (sec?.page === "platform_trust") return null;
              const sd = sec?.data;
              const ps = sd?.page_score;
              const m = sevMeta(ps?.severity);
              const alertMessage =
                sd?.alert_message || (sec as any)?.alert_message || (sd as any)?.alert;
              const hasAlert =
                typeof alertMessage === "string" && alertMessage.trim() !== "";
              const hasMiss =
                Object.keys(sd?.fields_to_improve || {})?.length > 0;
              const rawAreas =
                sd?.areas_for_improvement ||
                (sd as any)?.area_of_improvment ||
                (sd as any)?.area_of_improvement ||
                [];
              const areas: Improvement[] = Array.isArray(rawAreas)
                ? rawAreas
                : rawAreas && typeof rawAreas === "object"
                ? [rawAreas]
                : [];
              const recos = sd?.ai_recommendations || [];
              const insights =
                sd?.industry_insights || sd?.investor_insights || [];

              return (
                <div
                  className="pa-sec mt-3"
                  key={`sec_${index}`}
                  style={{ borderLeft: `6px solid ${m.c}` }}
                >
                  {/* Header */}
                  <div className="pa-sec-head">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="pa-sec-num">{index + 1}</div>
                        <h2 className="pa-sec-title">
                          {HelperService.ProperCase(sec?.page || "")}
                        </h2>
                        {hasAlert ? null : hasMiss ? (
                          <span className="pa-badge-warn">
                            AI Detected Missing Info
                          </span>
                        ) : (
                          <span className="pa-badge-ok">Profile Optimized</span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        {ps?.pre_admin_cap_total_score !== undefined && ps.pre_admin_cap_total_score !== ps.total_score && (
                          <span
                            className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 font-11 rounded"
                            title="Pre-Admin Cap Score"
                          >
                            Pre-Cap: {ps.pre_admin_cap_total_score}
                          </span>
                        )}
                        {ps?.total_score !== undefined && ps?.total_score !== null && (
                          <span
                            className="pa-score-chip"
                            style={{ background: m.bg, color: m.text }}
                          >
                            {ps?.total_score || 0}%
                          </span>
                        )}
                        <Button
                          className="pa-update-btn"
                          onClick={() => {
                            setSelectedSection(sec?.page);
                            setFieldsToImprove(sd?.fields_to_improve);
                            setShowMissingFieldsModal(true);
                          }}
                        >
                          <BiPencil size={16} />
                          Update {HelperService.ProperCase(sec?.page || "")}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="pa-sec-body">
                    {/* Critical Admin Cap Notice */}
                    {((ps?.score_adjustments && ps.score_adjustments.length > 0) ||
                      (ps?.admin_cap_adjustment_points !== undefined && ps.admin_cap_adjustment_points !== 0)) && (
                      <div
                        className="p-3 mb-3 rounded-3"
                        style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "4px solid #dc2626" }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                          <div className="d-flex align-items-center gap-2 text-danger fw-bold font-14">
                            <FiAlertTriangle size={18} />
                            Critical Admin Score Cap Applied
                          </div>
                          {ps.admin_cap_adjustment_points !== undefined && (
                            <span className="badge bg-danger text-white rounded-pill px-2.5 py-1">
                              {ps.admin_cap_adjustment_points > 0 ? `+${ps.admin_cap_adjustment_points}` : ps.admin_cap_adjustment_points} pts
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-3 font-13 text-secondary mb-2">
                          <span>Raw Score: <strong className="text-dark">{ps.pre_admin_cap_total_score ?? ps.total_score}</strong></span>
                          <span>➔</span>
                          <span>Capped Score: <strong className="text-danger">{ps.total_score}</strong></span>
                          <span className="ms-auto badge" style={{ background: m.bg, color: m.text }}>
                            {ps.profile_strength_label || "Weak"}
                          </span>
                        </div>
                        {ps.score_adjustments && ps.score_adjustments.length > 0 && (
                          <div className="d-flex flex-column gap-1.5 pt-1 border-top border-danger-subtle">
                            {ps.score_adjustments.map((adj, aIdx) => (
                              <div key={aIdx} className="d-flex align-items-center gap-2 font-12 text-danger-emphasis">
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle font-10">
                                  {HelperService.ProperCase((adj?.type || "Admin Cap").replace(/_/g, " "))}
                                </span>
                                <span>{adj.reason}</span>
                                {adj.points !== undefined && (
                                  <span className="ms-auto fw-bold">{adj.points > 0 ? `+${adj.points}` : adj.points} pts</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sub-Category Scoring Breakdown */}
                    {(() => {
                      const sb = ps?._score_breakdown || {};
                      const subItems = [
                        { label: "Completeness", score: ps?.completeness_score, max: sb.completeness_max ?? 97.5 },
                        { label: "Business Model", score: ps?.business_model_score, max: sb.business_model_max ?? 32.5 },
                        { label: "Funding Logic", score: ps?.funding_logic_score, max: sb.funding_logic_max ?? 55 },
                        { label: "Founder & Team", score: ps?.founder_team_score, max: sb.founder_team_max ?? 37.5 },
                        { label: "Trust & Compliance", score: ps?.trust_compliance_score, max: sb.trust_compliance_max ?? 40 },
                      ].filter((x): x is { label: string; score: number; max: number } => typeof x.score === "number");

                      if (subItems.length === 0) return null;

                      return (
                        <div className="mb-3 p-3 rounded-3 bg-light border">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-bold font-12 text-uppercase text-secondary">
                              Scoring Category Breakdown
                            </span>
                            {sb.master_framework && (
                              <span className="badge bg-success-subtle text-success border border-success-subtle font-10">
                                {String(sb.master_framework).replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                          <div className="row g-2">
                            {subItems.map((sub, sIdx) => {
                              const sVal = Number(sub.score) || 0;
                              const pct = sub.max > 0 ? Math.round((sVal / sub.max) * 100) : 0;
                              return (
                                <div key={sIdx} className="col-6 col-md">
                                  <div className="p-2 bg-white rounded border">
                                    <div className="d-flex justify-content-between font-11 text-muted mb-1">
                                      <span>{sub.label}</span>
                                      <span className="fw-bold text-dark">{pct}%</span>
                                    </div>
                                    <div className="fw-bold font-14 mb-1 text-primary">
                                      {sub.score} <span className="font-11 text-muted">/ {sub.max}</span>
                                    </div>
                                    <div className="progress" style={{ height: "4px" }}>
                                      <div
                                        className="progress-bar bg-success"
                                        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {hasAlert ? (
                      <div className="pa-alert-box">
                        <div className="d-flex align-items-center gap-3">
                          <FiAlertTriangle size={22} className="text-warning flex-shrink-0" />
                          <div className="pa-alert-text">{alertMessage}</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Areas for Improvement */}
                        <div className="pa-group-label pa-group-red">
                          <IconAreas />
                          Areas for Improvement
                        </div>
                        {areas.length > 0 ? (
                          <div className="row g-0">
                            {areas.map((item: Improvement, i: number) => {
                              const isInputRequired = Boolean(
                                item?.input_required === true ||
                                item?.input_required === "true" ||
                                String(item?.input_required).toLowerCase() === "true"
                              );

                              return (
                                <div className="col-md-6" key={`area_${i}`}>
                                  <div className="pa-li pe-md-3">
                                    <img
                                      src={severityIcons[item?.severity || "weak"]}
                                      className="pa-li-icon"
                                      alt="icon"
                                    />
                                    <div className="flex-grow-1">
                                      <div className="pa-li-title">{item?.title}</div>
                                      <p className="pa-li-desc">{item?.description}</p>
                                      {isInputRequired && (
                                        <button
                                          type="button"
                                          className="pa-give-input-btn"
                                          onClick={() =>
                                            handleOpenUserInputModal(
                                              item,
                                              sec?.page,
                                              (sd as any)?.business_info_id ?? (sec as any)?.business_info_id
                                            )
                                          }
                                        >
                                          <FiEdit3 size={13} />
                                          <span>{item?.action_text || item?.button_text || "Click here to add more details"}</span>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="pa-empty">No issues detected.</div>
                        )}

                        {/* AI Recommendations */}
                        <div className="pa-group-label pa-group-green mt-4">
                          <IconReco />
                          AI Recommendations
                        </div>
                        {recos.length > 0 ? (
                          <div className="row g-0">
                            {recos.map((item: Recommendation, i: number) => (
                              <div className="col-md-6" key={`reco_${i}`}>
                                <div className="pa-li pe-md-3">
                                  <img
                                    src={severityIcons[item?.priority || "ok"]}
                                    className="pa-li-icon"
                                    alt="icon"
                                  />
                                  <div>
                                    <div className="pa-li-title">{item?.title}</div>
                                    <p className="pa-li-desc">{item?.description}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="pa-empty">No recommendations.</div>
                        )}

                        {/* Industry Insights */}
                        <div className="pa-group-label pa-group-teal mt-4">
                          <IconInsights />
                          Industry Insights
                        </div>
                        {insights.length > 0 ? (
                          <div className="row g-0">
                            {insights.map((item: KeywordInsight, i: number) => (
                              <div className="col-md-6" key={`insight_${i}`}>
                                <div className="pa-li pe-md-3">
                                  <span
                                    className="pa-li-dot"
                                    style={{ background: "#0ea5a0" }}
                                  />
                                  <div>
                                    <div className="pa-li-title">{item?.title}</div>
                                    <p className="pa-li-desc">{item?.description}</p>
                                    {item?.keywords && item?.keywords.length > 0 && (
                                      <div>
                                        {item?.keywords.map(
                                          (k: string, ki: number) => (
                                            <span
                                              className="pa-chip"
                                              key={`k_${ki}`}
                                            >
                                              {k}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="pa-empty">No insights available.</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            },
          )}

          <div className="pa-foot">
            Generated by The Prime Shark · AI Shark Intelligence
          </div>
        </Container>
      )}

      {showMissingFieldsModal && (
        <UpdateProfileInfoModal
          showMissingFieldsModal={showMissingFieldsModal}
          setShowMissingFieldsModal={(flag) => {
            setShowMissingFieldsModal(flag);
            if (!flag) {
              getAnalysisResponse(false);
            }
          }}
          section={selectedSection}
          fields_to_improve={fieldsToImprove}
          account_type={account_type}
          login_type={login_type}
        />
      )}

      {showUserInputModal && selectedImprovementForInput && (
        <UserInputModal
          show={showUserInputModal}
          onHide={() => {
            setShowUserInputModal(false);
            setSelectedImprovementForInput(null);
            setSelectedImprovementBizInfoId(null);
          }}
          item={selectedImprovementForInput}
          sectionPage={userInputSectionPage}
          businessInfoId={selectedImprovementBizInfoId}
          businessId={selectedImprovementBizInfoId || businessId}
          businessUuid={businessUuid}
          onSuccess={() => {
            getAnalysisResponse(false);
          }}
        />
      )}
    </section>
  );
};

export default AnalysisPremium;
